import React, { useState, useEffect } from 'react';
import { Calculator, MapPin, Building2, Users, Euro, TrendingUp, Info, ChevronRight, Shield, PiggyBank, FileText, Check, Clock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';

import axios from 'axios';
import { toast } from 'sonner';
import ESSConsentModal, { useESSConsent } from './ESSConsentModal';
import { SECTEURS, TERRITOIRES, TYPES_ACTEURS, DUREES } from './SimulateurOscopConstants';
import SimulateurResults from './SimulateurResults';
import SimulateurOptionsStep from './SimulateurOptionsStep';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SimulateurMasterOSCOP({ onSimulationComplete, showHeader = true }) {

  // Hook de consentement ESS
  const { 
    hasConsent, 
    showModal, 
    handleAccept 
  } = useESSConsent('oscop_master_v1');
  
  // États du formulaire - Contexte
  const [territoire, setTerritoire] = useState('');
  const [secteur, setSecteur] = useState('');
  
  // États du formulaire - Profil
  const [typeActeur, setTypeActeur] = useState('Particulier');
  const [revenuMensuel, setRevenuMensuel] = useState('');
  const [chiffreAffaires, setChiffreAffaires] = useState('');
  
  // États du formulaire - Projet
  const [montantProjet, setMontantProjet] = useState('');
  const [apport, setApport] = useState('');
  const [dureeMois, setDureeMois] = useState(180);
  
  // États du formulaire - Options ESS
  const [adhesionOscop, setAdhesionOscop] = useState(true);
  const [adhesionFtpe, setAdhesionFtpe] = useState(false);
  const [activationPrevention, setActivationPrevention] = useState(true);
  
  // États de l'application
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1: Contexte, 2: Profil, 3: Projet, 4: Options, 5: Résultats
  const [_config, setConfig] = useState(null);
  
  // Charger la configuration
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/simulateur/oscop/master/config`);
        setConfig(response.data);
      } catch {
      }
    };
    loadConfig();
  }, []);
  
  // Déterminer si on doit montrer revenu ou CA
  const showRevenu = typeActeur === 'Particulier' || typeActeur === 'Exploitant';
  const showCA = typeActeur === 'TPE' || typeActeur === 'Exploitant' || typeActeur === 'Association';
  
  // Validation par étape
  const isStep1Valid = territoire && secteur;
  const isStep2Valid = (
    (showRevenu ? revenuMensuel && parseFloat(revenuMensuel) > 0 : true) &&
    (showCA ? chiffreAffaires && parseFloat(chiffreAffaires) > 0 : true)
  );
  const isStep3Valid = montantProjet && parseFloat(montantProjet) > 0;
  
  // Lancer la simulation
  const runSimulation = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const payload = {
        territoire,
        secteur,
        type_acteur: typeActeur,
        revenu_mensuel: showRevenu ? parseFloat(revenuMensuel) : null,
        chiffre_affaires: showCA ? parseFloat(chiffreAffaires) : null,
        montant_projet: parseFloat(montantProjet),
        apport: parseFloat(apport) || 0,
        duree_mois: dureeMois,
        adhesion_oscop: adhesionOscop,
        adhesion_ftpe: adhesionFtpe,
        activation_prevention: activationPrevention
      };
      
      const response = await axios.post(`${BACKEND_URL}/api/simulateur/oscop/master`, payload);
      
      if (response.data.status === 'blocked') {
        // Restriction HARD - Blocage
        setError({
          type: 'blocked',
          restriction_id: response.data.restriction_id,
          restriction_name: response.data.restriction_name,
          message: response.data.message,
          legal_notice: response.data.legal_notice
        });
        const lang = 'fr'; // ou détecter la langue
        toast.error(response.data.message?.[lang] || response.data.message?.fr || 'Simulation bloquée');
      } else if (response.data.status === 'refuse') {
        setError(response.data.message);
        toast.error(response.data.message);
      } else {
        setResult(response.data);
        setStep(5);
        
        // Notification selon le status
        if (response.data.status === 'ok_with_limits') {
          toast.warning('Simulation effectuée avec des ajustements');
        } else {
          toast.success('Simulation effectuée avec succès !');
        }
        
        if (onSimulationComplete) {
          onSimulationComplete(response.data);
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Erreur lors de la simulation';
      setError(errorMessage);
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Erreur lors de la simulation');
    } finally {
      setLoading(false);
    }
  };
  
  // Reset
  const resetSimulation = () => {
    setResult(null);
    setStep(1);
    setError(null);
  };
  
  // Navigation
  const nextStep = () => setStep(s => Math.min(s + 1, 4));
  const prevStep = () => setStep(s => Math.max(s - 1, 1));
  
  // Obtenir l'icône du secteur
  const getSecteurIcon = (s) => {
    const Icon = SECTEURS[s]?.icon || Building2;
    return <Icon className="h-5 w-5" />;
  };
  
  // Render Step 1: Contexte
  const renderStep1 = () => (
    <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 mb-3">
          <MapPin className="h-6 w-6 text-white" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">Contexte du projet</h3>
        <p className="text-gray-500">Sélectionnez votre territoire et secteur d'activité</p>
      </div>
      
      {/* Territoire */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Territoire *</Label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(TERRITOIRES).map(([name, cfg]) => (
            <button
              key={name}
              type="button"
              onClick={() => setTerritoire(name)}
              className={`p-4 rounded-xl border-2 transition-all duration-200 hover:scale-105 ${
                territoire === name
                  ? 'border-blue-500 bg-blue-50 shadow-lg'
                  : 'border-gray-200 hover:border-blue-200'
              }`}
            >
              <div className="text-2xl mb-1">{cfg.icon}</div>
              <div className="text-sm font-medium">{name}</div>
              <div className="text-xs text-gray-500">{cfg.code}</div>
            </button>
          ))}
        </div>
      </div>
      
      {/* Secteur */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Secteur d'activité *</Label>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(SECTEURS).map(([name, cfg]) => {
            const Icon = cfg.icon;
            return (
              <button
                key={name}
                type="button"
                onClick={() => setSecteur(name)}
                className={`p-4 rounded-xl border-2 transition-all duration-200 hover:scale-105 ${
                  secteur === name
                    ? `border-transparent bg-gradient-to-br ${cfg.color} text-white shadow-lg`
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Icon className={`h-6 w-6 mx-auto mb-2 ${secteur === name ? 'text-white' : cfg.textColor}`} />
                <div className="text-sm font-medium">{name}</div>
              </button>
            );
          })}
        </div>
      </div>
      
      <Button
        onClick={nextStep}
        disabled={!isStep1Valid}
        className="w-full h-12 text-lg bg-gradient-to-r from-blue-600 to-indigo-600"
      >
        Continuer <ChevronRight className="ml-2 h-5 w-5" />
      </Button>
    </div>
  );
  
  // Render Step 2: Profil
  const renderStep2 = () => (
    <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-purple-600 mb-3">
          <Users className="h-6 w-6 text-white" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">Votre profil</h3>
        <p className="text-gray-500">Renseignez vos informations économiques</p>
      </div>
      
      {/* Type d'acteur */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Type d'acteur *</Label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(TYPES_ACTEURS).map(([name, cfg]) => (
            <button
              key={name}
              type="button"
              onClick={() => setTypeActeur(name)}
              className={`p-4 rounded-xl border-2 transition-all ${
                typeActeur === name
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 hover:border-purple-200'
              }`}
            >
              <div className="text-2xl mb-1">{cfg.icon}</div>
              <div className="text-sm font-medium">{name}</div>
              <div className="text-xs text-gray-500">{cfg.description}</div>
            </button>
          ))}
        </div>
      </div>
      
      {/* Revenu mensuel */}
      {showRevenu && (
        <div className="space-y-2">
          <Label className="text-base font-semibold flex items-center gap-2">
            <Euro className="h-5 w-5 text-green-600" />
            Revenu mensuel *
          </Label>
          <div className="relative">
            <Input
              type="number"
              placeholder="Ex: 2000"
              value={revenuMensuel}
              onChange={(e) => setRevenuMensuel(e.target.value)}
              className="pl-10 text-lg h-12"
            />
            <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
        </div>
      )}
      
      {/* Chiffre d'affaires */}
      {showCA && (
        <div className="space-y-2">
          <Label className="text-base font-semibold flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-blue-600" />
            Chiffre d'affaires annuel *
          </Label>
          <div className="relative">
            <Input
              type="number"
              placeholder="Ex: 150000"
              value={chiffreAffaires}
              onChange={(e) => setChiffreAffaires(e.target.value)}
              className="pl-10 text-lg h-12"
            />
            <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
        </div>
      )}
      
      <div className="flex gap-3">
        <Button variant="outline" onClick={prevStep} className="flex-1">
          Retour
        </Button>
        <Button
          onClick={nextStep}
          disabled={!isStep2Valid}
          className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600"
        >
          Continuer <ChevronRight className="ml-2 h-5 w-5" />
        </Button>
      </div>
    </div>
  );
  
  // Render Step 3: Projet
  const renderStep3 = () => (
    <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 mb-3">
          <FileText className="h-6 w-6 text-amber-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">Votre projet</h3>
        <p className="text-gray-500">Décrivez les paramètres financiers de votre projet</p>
      </div>
      
      {/* Montant du projet */}
      <div className="space-y-2">
        <Label className="text-base font-semibold flex items-center gap-2">
          <Euro className="h-5 w-5 text-amber-600" />
          Montant du projet *
        </Label>
        <div className="relative">
          <Input
            type="number"
            placeholder="Ex: 120000"
            value={montantProjet}
            onChange={(e) => setMontantProjet(e.target.value)}
            className="pl-10 text-lg h-12"
          />
          <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        </div>
      </div>
      
      {/* Apport */}
      <div className="space-y-2">
        <Label className="text-base font-semibold flex items-center gap-2">
          <PiggyBank className="h-5 w-5 text-green-600" />
          Apport personnel
        </Label>
        <div className="relative">
          <Input
            type="number"
            placeholder="Ex: 10000"
            value={apport}
            onChange={(e) => setApport(e.target.value)}
            className="pl-10 text-lg h-12"
          />
          <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        </div>
      </div>
      
      {/* Durée */}
      <div className="space-y-3">
        <Label className="text-base font-semibold flex items-center gap-2">
          <Clock className="h-5 w-5 text-blue-600" />
          Durée de financement
        </Label>
        <div className="grid grid-cols-5 gap-2">
          {DUREES.map((d) => (
            <button
              key={d.mois}
              type="button"
              onClick={() => setDureeMois(d.mois)}
              className={`p-3 rounded-lg border-2 transition-all ${
                dureeMois === d.mois
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-blue-200'
              }`}
            >
              <div className="text-lg font-bold">{d.mois/12}</div>
              <div className="text-xs">ans</div>
            </button>
          ))}
        </div>
      </div>
      
      <div className="flex gap-3">
        <Button variant="outline" onClick={prevStep} className="flex-1">
          Retour
        </Button>
        <Button
          onClick={nextStep}
          disabled={!isStep3Valid}
          className="flex-1 bg-gradient-to-r from-amber-600 to-orange-600"
        >
          Continuer <ChevronRight className="ml-2 h-5 w-5" />
        </Button>
      </div>
    </div>
  );
  
  
  // Progress indicator
  const renderProgress = () => (
    <div className="flex items-center justify-center gap-2 mb-8">
      {[1, 2, 3, 4].map((s) => (
        <React.Fragment key={s}>
          <button
            onClick={() => s < step && setStep(s)}
            className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
              step === s ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white scale-110' :
              step > s ? 'bg-green-500 text-white cursor-pointer hover:scale-105' :
              'bg-gray-200 text-gray-500'
            }`}
          >
            {step > s ? <Check className="h-5 w-5" /> : s}
          </button>
          {s < 4 && <div className={`w-8 md:w-16 h-1 ${step > s ? 'bg-green-500' : 'bg-gray-200'}`} />}
        </React.Fragment>
      ))}
    </div>
  );
  
  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Modal de consentement ESS */}
      <ESSConsentModal
        isOpen={showModal && !hasConsent}
        onAccept={handleAccept}
        onClose={() => {}}
        simulatorId="oscop_master_v1"
        simulatorName="Simulateur Coopératif Multisectoriel O'SCOP"
      />
      
      <Card className="overflow-hidden shadow-2xl border-0">
        {/* Header */}
        {showHeader && (
          <CardHeader className="bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900 text-white p-8">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-white/10 rounded-2xl backdrop-blur">
                <Calculator className="h-10 w-10 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl md:text-3xl font-bold mb-2">
                  Simulateur Coopératif Multisectoriel O'SCOP
                </CardTitle>
                <CardDescription className="text-white/80 text-base">
                  Aide à la décision économique coopérative • Non engageant
                </CardDescription>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2 mt-6">
              {Object.entries(SECTEURS).map(([name, cfg]) => {
                const Icon = cfg.icon;
                return (
                  <Badge key={name} className={`${cfg.bgColor} text-white border-0`}>
                    <Icon className="h-3 w-3 mr-1" /> {name}
                  </Badge>
                );
              })}
            </div>
          </CardHeader>
        )}
        
        {/* Contenu - Affiché uniquement après consentement */}
        <CardContent className="p-6 md:p-8">
          {hasConsent ? (
            <>
              {step < 5 && renderProgress()}
              
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}
              {step === 4 && (
                <SimulateurOptionsStep
                  territoire={territoire}
                  secteur={secteur}
                  getSecteurIcon={getSecteurIcon}
                  montantProjet={montantProjet}
                  dureeMois={dureeMois}
                  adhesionOscop={adhesionOscop}
                  setAdhesionOscop={setAdhesionOscop}
                  adhesionFtpe={adhesionFtpe}
                  setAdhesionFtpe={setAdhesionFtpe}
                  activationPrevention={activationPrevention}
                  setActivationPrevention={setActivationPrevention}
                  error={error}
                  prevStep={prevStep}
                  runSimulation={runSimulation}
                  loading={loading}
                />
              )}
              {step === 5 && <SimulateurResults result={result} resetSimulation={resetSimulation} />}
            </>
          ) : (
            <div className="text-center py-12">
              <Shield className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                Veuillez accepter les conditions d'utilisation pour accéder au simulateur.
              </p>
            </div>
          )}
        </CardContent>
        
        {/* Footer */}
        <CardFooter className="bg-gray-50 border-t px-6 py-4">
          <p className="text-xs text-gray-500 text-center w-full">
            <Info className="h-3 w-3 inline mr-1" />
            Simulation indicative ESS • Outil d'aide à la décision coopérative • Ne constitue ni une offre, ni un engagement • Hors Code des assurances
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
